import type { SearchKeyDomain } from '@shared/core/search';
import algoliasearch, { SearchClient } from 'algoliasearch';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react';
import { useAuth } from 'src/auth';
import {
  type AlgoliaKeyManager,
  createAlgoliaKeyManager,
  fetchAlgoliaCredentials,
} from 'src/providers/algoliaKeyManager';
import { createMemoryCache, createNullCache } from 'src/utils/algoliaCacheMethods';
import { LedgerEntryDetails, LedgerEntryGroup, LotStatus, PurchaseOrderStatus, SalesOrderStatus } from 'types/graphql';

/* Keep in sync with algolia.ts in /api/ */

export enum SearchIndex {
  Products = 'products',
  Lots = 'lots',
  Orders = 'orders',
  BusinessEntities = 'businessEntities',
  Places = 'places',
  Contacts = 'contacts',
  ProductionRuns = 'productionRuns',
  LedgerEntries = 'ledgerEntries',
  LedgerAccounts = 'ledgerAccounts',
}

export type ProductIndex = {
  objectID: string;
  description: string;
  organizationId: number;
  productId: number;
  productCode: string;
  commodity: string;
  commodityLabel: string;
  commodityUnit: string;
  commoditySize: string;
  commodityStyle: string;
  countryOfOrigin: string;
  disabled: boolean;
  inventory: Record<number, Record<string | 'latest', { available?: number; reserved?: number }>>;
  variableWeight: boolean;
  weightPerInventoryUnit: number;
  cartonsPerPallet: number | null;
};

export type PurchaseOrderIndex = {
  orderId: number;
  organizationId: number;
  slug: string;
  objectID: string;
  status: PurchaseOrderStatus;
  vendor: string;
  vendorSO: string;
  salesPerson: string;
  contact: string;
  shippedFrom: string;
};

export type SalesOrderIndex = {
  orderId: number;
  organizationId: number;
  slug: string;
  objectID: string;
  buyer: string;
  status: SalesOrderStatus;
  customerPO: string;
  salesPerson: string;
  contact: string;
  shipTo: string;
};

export type OrderReference = { purchaseOrderId?: number; salesOrderId?: number; buySellOrderId?: number; slug: string };

export type PalletTagReference = { tagNumber: string; tagNumbers: string[]; palletTagId: number };

export type LotIndex = {
  organizationId: number;
  slug: string;
  lotId: number;
  productId: number;
  description: string;
  objectID: string;
  orders: OrderReference[];
  status: LotStatus;
  receiveDate: string;
  receiveDateTimestamp: number;
  receivedByWeight: boolean;
  weight: number;
  quantity: number;
  reserved: number;
  vendor: string;
  grower: string;
  palletTags: PalletTagReference[];
  // Only repacked lots carry this, so it is absent on most hits.
  repackDate?: string | null;
  warehouseId: number;
  commodity: string;
  commodityStyle: string;
  commoditySize: string;
  commodityUnit: string;
  commodityLabel: string;
  organic: boolean;
  countryOfOrigin: string;
  grade: string;
};

export type BusinessEntityIndex = { businessEntityId: number; organizationId: number; objectID: string; name: string };

export type LedgerAccountIndex = {
  objectID: string; // aka fragment account path
  organizationId: number;
  path: string[];
  fullPath: string; // cannot facet an array
  displayPath: string[];
  number: string;
  name: string;
  type: string;
  updatedAt: number; // epoch
  currentBalance: number;
};

export type LedgerEntryIndex = LedgerEntryGroup & {
  objectID: string; // aka the fragment entry id
  accountPaths: string[];
  counterParties: string[];
  entry: LedgerEntryDetails & {
    postedAt: number;
    createdAt: number; // useful for stable sorting
  };
};

export const AlgoliaContext = createContext<SearchClient>(undefined);
const AlgoliaKeyManagerContext = createContext<AlgoliaKeyManager | undefined>(undefined);

export default function AlgoliaContextProvider({ children }: PropsWithChildren) {
  const { currentUser, getToken } = useAuth();
  const flags = useFlags();
  const cacheDisabled = flags.disableAlgoliaCache;

  const cacheOptions = useMemo(() => {
    if (cacheDisabled) {
      return { requestsCache: createNullCache(), responsesCache: createNullCache() };
    }

    return { requestsCache: createMemoryCache(), responsesCache: createMemoryCache() };
  }, [cacheDisabled]);

  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  });

  const identity = currentUser ? `${currentUser.id}:${currentUser.organizationId ?? ''}` : null;
  const isGuest = Boolean(currentUser?.guest);

  const manager = useMemo(() => {
    if (!identity) return undefined;
    return createAlgoliaKeyManager({
      fetchCredentials: async () => (isGuest ? null : fetchAlgoliaCredentials(() => getTokenRef.current())),
      createClient: (apiKey) => algoliasearch(process.env.ALGOLIA_CLIENT_ID ?? '', apiKey, cacheOptions),
    });
  }, [identity, isGuest, cacheOptions]);

  useEffect(() => manager?.start(), [manager]);

  return (
    <AlgoliaKeyManagerContext.Provider value={manager}>
      <AlgoliaContext.Provider value={manager?.client as SearchClient}>{children}</AlgoliaContext.Provider>
    </AlgoliaKeyManagerContext.Provider>
  );
}

export const useAlgolia = () => useContext(AlgoliaContext);

export const useAlgoliaClient = (domain: SearchKeyDomain): SearchClient | undefined =>
  useContext(AlgoliaKeyManagerContext)?.domainClient(domain);
